import { FC, useState } from "react";
import { contactUsRequest } from "../../../types/commonTypes";
import { PiClipboardText } from "react-icons/pi";
import { Button, message, Modal } from "antd";
import { useMutation } from "@tanstack/react-query";
import { adminChangeContactUsRequestStatus } from "../../../network/admin";
import queryClient from "../../../services/react-query/queryClient";
import { RiMessage3Line } from "react-icons/ri";

interface ContactUsDetailsProps {
  request: contactUsRequest;
  formattedStatus?: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "new_lead":
      return "#666";
    case "contacted":
      return "#0800D9";
    case "qualified":
      return "#CF953D";
    case "closed_won":
      return "#479821";
    case "closed_lost":
      return "red";
    default:
      return "#666";
  }
};
const ContactUsDetails: FC<ContactUsDetailsProps> = ({
  request,
  formattedStatus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState(request.status);
  const { mutate, isLoading } = useMutation(
    () => {
      return adminChangeContactUsRequestStatus(request?.id, { status });
    },
    {
      onSuccess: (res: any) => {
        console.log(res);
        if (
          res &&
          res?.code !== "ERR_BAD_REQUEST" &&
          res?.code !== "ERR_BAD_RESPONSE"
        ) {
          message.success("Request status changed successfully");
          queryClient.invalidateQueries(["contactUsRequests"]);
          setIsOpen(false);
        } else {
          message.error(res?.response?.data?.message || "Something went wrong");
        }
      },
      onError: (error: any) => {
        console.log(error);
        message.error("Something went wrong");
      },
    }
  );
  const handleStatusChange = () => {
    mutate();
  };
  return (
    <div className="p-6">
      <Modal
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
        width={700}
        centered
      >
        <div className="space-y-8">
          <p>Change Request Status to</p>
          <div className="flex gap-4 flex-wrap justify-center">
            <Button
              htmlType="button"
              style={{
                border: "1px solid ",
                borderColor: "#666",
                color: "#666",
              }}
              onClick={() => setStatus("new_lead")}
              disabled={request.status === "new_lead"}
              className={status === "new_lead" ? "font-bold" : ""}
            >
              New Lead
            </Button>
            <Button
              htmlType="button"
              style={{
                border: "1px solid ",
                borderColor: "#0800D9",
                color: "#0800D9",
              }}
              disabled={request.status === "contacted"}
              onClick={() => setStatus("contacted")}
              className={status === "contacted" ? "font-bold" : ""}
            >
              Contacted
            </Button>
            <Button
              htmlType="button"
              style={{
                border: "1px solid ",
                borderColor: "#CF953D",
                color: "#CF953D",
              }}
              onClick={() => setStatus("qualified")}
              disabled={request.status === "qualified"}
              className={status === "qualified" ? "font-bold" : ""}
            >
              Qualified
            </Button>
            <Button
              htmlType="button"
              style={{
                border: "1px solid ",
                borderColor: "#479821",
                color: "#479821",
              }}
              onClick={() => setStatus("closed_won")}
              disabled={request.status === "closed_won"}
              className={status === "closed_won" ? "font-bold" : ""}
            >
              Closed(Won)
            </Button>
            <Button
              htmlType="button"
              danger
              onClick={() => setStatus("closed_lost")}
              disabled={request.status === "closed_lost"}
              className={status === "closed_lost" ? "font-bold" : ""}
            >
              Closed(Lost)
            </Button>
          </div>
          <div className="flex gap-4">
            <Button
              htmlType="button"
              type="primary"
              block
              loading={isLoading}
              onClick={handleStatusChange}
              disabled={status === request.status}
            >
              Save
            </Button>
            <Button
              htmlType="button"
              block
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <div className="space-y-4">
        <div className="flex gap-4 justify-between items-center ">
          <div className="flex items-start gap-2">
            <p>
              <PiClipboardText size={25} color="gray" />
            </p>{" "}
            <p> Request Details</p>
          </div>
          <div className="">
            <Button
              htmlType="button"
              onClick={() => setIsOpen(true)}
              style={{
                border: "1px solid ",
                borderColor: getStatusColor(request?.status),
                color: getStatusColor(request?.status),
              }}
            >
              {formattedStatus}
            </Button>
          </div>
        </div>
        <div className="flex justify-between p-2 px-4 bg-[#EEF0FF] rounded-lg">
          <p className="text-color-primary"> Name</p>
          <p>{request?.name || request?.business_name}</p>
        </div>
        <div>
          <p className="flex justify-between p-2 px-4">
            <span className="text-color-sec-title">Form Name</span>
            <span className="text-color-primary">{request?.form_name}</span>
          </p>
          <p className="flex justify-between p-2 px-4">
            <span className="text-color-sec-title">Email Address</span>
            <span className="text-color-primary">{request?.email}</span>
          </p>
          <p className="flex justify-between p-2 px-4">
            <span className="text-color-sec-title">Phone Number</span>
            <span className="text-color-primary">{request?.phone}</span>
          </p>
          <p className="flex justify-between p-2 px-4">
            <span className="text-color-sec-title">Type</span>
            <span className="text-color-primary">{request?.salon_type}</span>
          </p>
          <p className="flex justify-between p-2 px-4">
            <span className="text-color-sec-title">Team Size</span>
            <span className="text-color-primary">{request?.team_size}</span>
          </p>
          <p className="flex justify-between p-2 px-4">
            <span className="text-color-sec-title">City</span>
            <span className="text-color-primary">
              {request?.business_location}
            </span>
          </p>
        </div>
        <div className="space-y-4 p-2 px-4 bg-[#EEF0FF] rounded-lg">
          <div className="flex items-center gap-2">
            <RiMessage3Line size={20} className="text-color-primary" />

            <p className="text-color-primary"> Message</p>
          </div>
          <p className="text-color-sec-title">{request?.message}</p>
        </div>
      </div>
    </div>
  );
};

export default ContactUsDetails;
